import { clsx } from 'clsx';
import { FAVORED_ENEMIES, getFavoredEnemyCount, getFavoredEnemy } from '@character-creator/data';

interface FavoredEnemySelectorProps {
  rangerLevel: number;
  selectedEnemies: string[];
  onToggle: (enemyId: string) => void;
  compact?: boolean;
}

export function FavoredEnemySelector({
  rangerLevel,
  selectedEnemies,
  onToggle,
  compact = false,
}: FavoredEnemySelectorProps) {
  const allowedCount = getFavoredEnemyCount(rangerLevel);
  const selectedCount = selectedEnemies.length;
  const needsMore = selectedCount < allowedCount;

  // Get selected enemy names for compact display
  const selectedNames = selectedEnemies
    .map((id) => getFavoredEnemy(id)?.name)
    .filter(Boolean);

  // Compact view - shows selected enemies
  if (compact && !needsMore && selectedNames.length > 0) {
    return (
      <div className="text-xs text-success">
        <span>Favored Enemies: {selectedNames.join(', ')}</span>
      </div>
    );
  }

  return (
    <div>
      <h5 className="text-sm font-medium text-text-primary mb-2">
        Favored Enemy
        {needsMore && (
          <span className="text-warning ml-2 text-xs">
            (Choose {allowedCount - selectedCount} more)
          </span>
        )}
      </h5>
      <p className="text-xs text-text-muted mb-3">
        You have advantage on Wisdom (Survival) checks to track your favored enemies and on
        Intelligence checks to recall information about them.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {FAVORED_ENEMIES.map((enemy) => {
          const isSelected = selectedEnemies.includes(enemy.id);
          const isDisabled = !isSelected && selectedCount >= allowedCount;

          return (
            <button
              key={enemy.id}
              type="button"
              onClick={() => onToggle(enemy.id)}
              disabled={isDisabled}
              className={clsx(
                'w-full text-left p-2 rounded-lg border transition-all duration-200',
                isSelected
                  ? 'bg-accent-gold/10 border-accent-gold'
                  : isDisabled
                    ? 'bg-background-secondary border-background-elevated opacity-50 cursor-not-allowed'
                    : 'bg-background-secondary border-background-elevated hover:border-text-muted'
              )}
            >
              <div className="font-medium text-text-primary text-sm">{enemy.name}</div>
              {enemy.examples && (
                <p className="text-xs text-text-muted mt-0.5">{enemy.examples}</p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
